import type { Round, RoundNames } from '../schemas/entities';
import type { Format, Segment, SideIndex, Slot } from '../schemas/format';

/** Which side index holds a slot this round, or null if the coin flip hasn't been recorded. */
export const sideOfSlot = (round: Pick<Round, 'firstSide' | 'format'>, slot: Slot): SideIndex | null => {
  const first = round.format.order === 'fixed' ? 0 : round.firstSide;
  if (first === null) return null;
  return slot === 'first' ? first : ((1 - first) as SideIndex);
};

export const sideLabel = (format: Format, side: SideIndex | null, fallback: string) =>
  side === null ? fallback : format.sideLabels[side];

export const slotLabel = (round: Pick<Round, 'firstSide' | 'format'>, slot: Slot) =>
  sideLabel(round.format, sideOfSlot(round, slot), slot === 'first' ? '1st' : '2nd');

/** Team letter on a given side. */
export const teamOnSide = (round: Pick<Round, 'teamASide'>, side: SideIndex): 'A' | 'B' | null =>
  round.teamASide === null ? null : round.teamASide === side ? 'A' : 'B';

export const segmentSide = (round: Pick<Round, 'firstSide' | 'format'>, seg: Segment): SideIndex | null =>
  seg.by === 'both' ? null : sideOfSlot(round, seg.by);

/** e.g. "C2 · Con Constructive" */
export const segmentTitle = (round: Pick<Round, 'firstSide' | 'format'>, seg: Segment) =>
  seg.by === 'both' ? seg.label : `${slotLabel(round, seg.by)} ${seg.label}`;

/**
 * Who gives a speech: team code plus the debaters' names for that speech's speaker numbers (PF: constructive and summary
 * are speaker 1, rebuttal and final focus are speaker 2). null until sides are known, and for crossfires.
 */
export const segmentSpeakers = (
  round: Pick<Round, 'firstSide' | 'format' | 'teamASide' | 'teamA' | 'teamB'>,
  seg: Segment,
  names?: Pick<RoundNames, 'teamA' | 'teamB'> | null,
): { team: string; names: string[] } | null => {
  if (seg.by === 'both') return null;
  const side = sideOfSlot(round, seg.by);
  const team = side === null ? null : teamOnSide(round, side);
  if (!team) return null;
  const list = (team === 'A' ? names?.teamA : names?.teamB) ?? [];
  return {
    team: (team === 'A' ? round.teamA : round.teamB).code,
    names: seg.speakers.map((n) => list[n - 1]?.trim() ?? '').filter(Boolean),
  };
};

export const sheetTitle = (round: Pick<Round, 'firstSide' | 'format'>, title: string) =>
  title.replace('{first}', slotLabel(round, 'first')).replace('{second}', slotLabel(round, 'second'));

/**
 * Resolve a coin flip into sides + order.
 * The winner picks either a side or a speaking position; the loser picks the other dimension.
 */
export interface CoinFlipInput {
  winner: 'A' | 'B';
  winnerChose: 'side' | 'order';
  /** If winnerChose side: the side the winner took. Otherwise the side the loser took. */
  sideTakenBy: { team: 'A' | 'B'; side: SideIndex };
  /** If winnerChose order: 'first'|'second' for the winner. Otherwise for the loser. */
  orderTakenBy: { team: 'A' | 'B'; slot: Slot };
}

export const resolveCoinFlip = (input: CoinFlipInput): { teamASide: SideIndex; firstSide: SideIndex } => {
  const teamASide: SideIndex = input.sideTakenBy.team === 'A' ? input.sideTakenBy.side : ((1 - input.sideTakenBy.side) as SideIndex);
  const teamAFirst = input.orderTakenBy.team === 'A' ? input.orderTakenBy.slot === 'first' : input.orderTakenBy.slot === 'second';
  const firstSide: SideIndex = teamAFirst ? teamASide : ((1 - teamASide) as SideIndex);
  return { teamASide, firstSide };
};

/**
 * Ballot keys ("A0", "B1": team letter + speaker index) of whoever is speaking in this segment. Crossfire and
 * cross-ex list both sides' participants. Empty until sides are known.
 */
export const speakerKeys = (round: Pick<Round, 'firstSide' | 'format' | 'teamASide'>, seg: Segment | null): string[] => {
  if (!seg) return [];
  const slots: Slot[] = seg.by === 'both' ? ['first', 'second'] : [seg.by];
  const nums = seg.speakers.length ? seg.speakers : Array.from({ length: round.format.teamSize }, (_, i) => i + 1);
  return slots.flatMap((slot) => {
    const side = sideOfSlot(round, slot);
    const team = side === null ? null : teamOnSide(round, side);
    return team ? nums.map((n) => `${team}${n - 1}`) : [];
  });
};
